import { assertValue, isEnumValue, roundNumberToFractionDigits } from '@shared/helpers'
import {
  ApplicantRole,
  ApplicationsMetaData,
  BackgroundInfoData,
  ENDPOINT_SEC_LOANS_EXIT,
  getSaleIncomeAndRemainingLoan,
  getTotalAspSavings,
  HousingInfoData,
  IfpLoan,
  LoanAmounts,
  OtherOccupation,
  SecLoanApplicationState,
  SecLoanHousingType,
  SecLoansApplicants,
  SecLoansEntry,
  SecLoansFlow,
  SecLoansHousingLoanTargetType,
  SecLoansSource,
} from '@shared/sec-loans'
import { OptionalReturnType } from '@shared/typeHelpers'
import { AuthenticationMode, Language, Occupation } from '@shared/types'
import { MAX_REASONABLE_NUMBER, MIN_REASONABLE_NUMBER } from '@shared/validation'
import { OTHER_VALUE } from '@ui-common/constants'
import { InputParams } from '@ui-common/steps/InitializeStep/initializeStepMachine'
import { CommonQueryParam } from '@ui-common/types'
import { callEndpoint } from '@ui-common/utils'
import {
  commonExit,
  debugLog,
  getEntry,
  getFlow,
  getSourceChannel,
  numberToString,
  resolveAndStoreParam,
  toNumber,
} from '@ui-common/utils/helpers'
import { resolveAndStoreLanguage } from '@ui-common/utils/language'
import { getLocalStorageItem } from '@ui-common/utils/storage'
import { ColoredLabelProps } from '@ui-components/dataDisplay/ColoredLabel'
import { IntlShape } from 'react-intl'

import { FALLBACK_EXIT_URLS } from '../config'
import { SECOND_APPLICANT_PREFIX } from '../constants/constants'
import { resolveAndStoreLoanOfferId } from '../loan-offer/helpers'
import { QueryParams } from '../loan-offer/types'
import { User, ValidatedData, WizardData } from '../wizard/types'
import { getMapEnum } from './getMapEnum'
import { assertSecLoansFlow, isSecLoansFlow, isSecLoansSourceChannel } from './typeHelpers'

export const applicantNumber = (hasCoApplicant: boolean, isSecondApplicant: boolean): string => {
  if (hasCoApplicant) {
    return isSecondApplicant ? ' (2)' : ' (1)'
  }
  return ''
}

export const getApplicantColoredLabel = (
  hasCoApplicant: boolean,
  applicant: SecLoansApplicants,
  formatMessage: IntlShape['formatMessage'],
): ColoredLabelProps | undefined => {
  if (hasCoApplicant) {
    const { ApplicantStickerColors } = getMapEnum(formatMessage)
    return {
      color: ApplicantStickerColors[applicant],
      text: formatMessage({ id: `sec-loans-two-applicants-${applicant}-applicant-label` }),
    }
  }
  return undefined
}

export const getApplicant = (isSecondApplicant: boolean): SecLoansApplicants => {
  return isSecondApplicant ? SecLoansApplicants.SecondApplicant : SecLoansApplicants.FirstApplicant
}

export const getApplicantPrefix = (isSecondApplicant: boolean): '' | typeof SECOND_APPLICANT_PREFIX => {
  return isSecondApplicant ? SECOND_APPLICANT_PREFIX : ''
}

export const resolveAndStoreSourceChannel = (): SecLoansSource => {
  return resolveAndStoreParam(CommonQueryParam.SourceChannel, Object.values(SecLoansSource), SecLoansSource.Unknown)
}

export const resolveAndStoreEntry = (): SecLoansEntry => {
  return resolveAndStoreParam(CommonQueryParam.Entry, Object.values(SecLoansEntry), SecLoansEntry.Default)
}

export const resolveAndStoreFlow = (): SecLoansFlow => {
  return resolveAndStoreParam(CommonQueryParam.Flow, Object.values(SecLoansFlow), SecLoansFlow.Default)
}

export const isEmployee = (backgroundInfoData: BackgroundInfoData | undefined, isSecondApplicant: boolean): boolean => {
  const data = isSecondApplicant ? backgroundInfoData?.secondApplicant : backgroundInfoData
  if (!data) {
    return false
  }
  const { occupation, hasValidEmploymentRelationship } = data
  return (
    occupation === Occupation.Employed ||
    (occupation === OtherOccupation.OnFamilyLeave && !!hasValidEmploymentRelationship)
  )
}

export const isEntrepreneur = (
  backgroundInfoData: BackgroundInfoData | undefined,
  isSecondApplicant: boolean,
): boolean => {
  const data = isSecondApplicant ? backgroundInfoData?.secondApplicant : backgroundInfoData
  if (!data) {
    return false
  }
  return data.occupation === Occupation.Entrepreneur
}

export const isHomeOwner = (housingInfoData: HousingInfoData | undefined, isSecondApplicant: boolean): boolean => {
  const data = isSecondApplicant ? housingInfoData?.secondApplicant : housingInfoData
  if (!data) {
    return false
  }
  return data.housingType === SecLoanHousingType.HomeOwnership
}

export const getAndValidateApplicantData = <T extends { secondApplicant?: Y }, Y extends object>(
  validateApplicant: (isSecondApplicant: boolean) => boolean,
  hasCoApplicant: boolean | undefined,
  data: T | undefined,
): ValidatedData<T | undefined | { secondApplicant?: Y }> | undefined => {
  const isFirstApplicantValid = validateApplicant(false)
  const isSecondApplicantValid = hasCoApplicant ? validateApplicant(true) : false
  if (!isFirstApplicantValid && !isSecondApplicantValid) {
    return
  }
  const firstApplicantData = isFirstApplicantValid ? data : undefined

  if (!hasCoApplicant) {
    return getValidatedData(firstApplicantData, isFirstApplicantValid)
  }

  return {
    data: {
      ...firstApplicantData,
      secondApplicant: isSecondApplicantValid ? data?.secondApplicant : undefined,
    },
    isComplete: getCompletenessForTwoApplicants({ hasCoApplicant, isFirstApplicantValid, isSecondApplicantValid }),
  }
}

export const getCompletenessForTwoApplicants = (params: {
  hasCoApplicant: boolean
  isFirstApplicantValid: boolean
  isSecondApplicantValid: boolean
}): boolean => {
  const { hasCoApplicant, isFirstApplicantValid, isSecondApplicantValid } = params

  return !!(isFirstApplicantValid && (!hasCoApplicant || isSecondApplicantValid))
}

export const getValidatedData = <T>(
  data: T | undefined,
  isValid: boolean,
  keepInvalidData = false,
): ValidatedData<T | undefined> => {
  return {
    data: isValid || keepInvalidData ? data : undefined,
    isComplete: isValid,
  }
}

export const getOptionAndOtherFormValues = (
  value: number | undefined,
  minValue = MIN_REASONABLE_NUMBER,
  maxValue = MAX_REASONABLE_NUMBER,
): { option?: string; other?: string } => {
  const isOther = value !== undefined && (value < minValue || value > maxValue)
  const option = isOther ? OTHER_VALUE : numberToString(value)
  const other = isOther ? numberToString(value) : undefined
  return { option, other }
}

export const resolveValueFromFormFields = (option: string, other: string | undefined | null): number | undefined => {
  if (option === OTHER_VALUE) {
    return toNumber(other)
  }
  return toNumber(option)
}

export const isTargetTypeApartment = (targetType: SecLoansHousingLoanTargetType): boolean =>
  targetType === SecLoansHousingLoanTargetType.HousingStock
export const isTargetTypeRightOfResidence = (targetType: SecLoansHousingLoanTargetType): boolean =>
  targetType === SecLoansHousingLoanTargetType.RightOfResidence
export const isTargetTypePartOwnership = (targetType: SecLoansHousingLoanTargetType): boolean =>
  targetType === SecLoansHousingLoanTargetType.PartOwnership

export const getLoanAmounts = (
  wizardData: Pick<WizardData, 'applicantInfo' | 'targetInfo'>,
  totalCost: number = 0,
  ownMoney: number = 0,
): LoanAmounts => {
  const { targetInfo, applicantInfo } = wizardData ?? {}
  const { loanCostEstimateInfo } = targetInfo ?? {}
  const aspSavingsTotalAmount = getTotalAspSavings(applicantInfo?.backgroundInfoData)

  const currentResidenceInfoData = [
    applicantInfo?.currentResidenceInfoData,
    applicantInfo?.currentResidenceInfoData?.secondApplicant,
  ]

  const { saleIncomeBefore, saleIncomeAfter, remainingLoan } = getSaleIncomeAndRemainingLoan(currentResidenceInfoData)
  const ownFinancingShare = loanCostEstimateInfo?.ownFinancingShare ?? 0
  const ownWorkShare = loanCostEstimateInfo?.ownWorkShare ?? 0

  const loanAmount = roundNumberToFractionDigits(
    Math.max(0, totalCost - ownMoney - aspSavingsTotalAmount - saleIncomeBefore - ownFinancingShare - ownWorkShare),
  )
  const finalLoanAmount = saleIncomeAfter
    ? roundNumberToFractionDigits(Math.max(0, loanAmount - saleIncomeAfter))
    : undefined

  const remainingLoanAmount = remainingLoan ? roundNumberToFractionDigits(loanAmount + remainingLoan) : undefined
  const temporaryLoanAmount = finalLoanAmount === 0 ? loanAmount : remainingLoanAmount

  return {
    loanAmount,
    temporaryLoanAmount,
    finalLoanAmount,
  }
}

export const isPrimaryApplicantWaitingSecondaryApplicant = (
  applicantRole: ApplicantRole,
  applicationState: SecLoanApplicationState,
): boolean => {
  return (
    applicantRole === ApplicantRole.PrimaryApplicant &&
    applicationState === SecLoanApplicationState.WaitingForSecondaryApplicant
  )
}

export const getAuthenticationMode = (sourceChannel: SecLoansSource): AuthenticationMode => {
  const authModes: Record<SecLoansSource, AuthenticationMode> = {
    [SecLoansSource.Netbank]: AuthenticationMode.Sso,
    [SecLoansSource.SPankkiFi]: AuthenticationMode.Interactive,
    [SecLoansSource.Extranet]: AuthenticationMode.Sso,
    [SecLoansSource.SMobile]: AuthenticationMode.Sso,
    [SecLoansSource.SMobileBrowser]: AuthenticationMode.Interactive,
    [SecLoansSource.Unknown]: AuthenticationMode.Interactive,
  }
  return authModes[sourceChannel]
}

export const getSecLoansSourceChannel = (): SecLoansSource => {
  const sourceChannel = getSourceChannel()
  return isSecLoansSourceChannel(sourceChannel) ? sourceChannel : SecLoansSource.Unknown
}

export const getSecLoansFlow = <TRequired extends boolean>(
  isRequired?: TRequired,
): OptionalReturnType<TRequired, SecLoansFlow> => {
  const flow = getFlow()

  if (isRequired) {
    return assertSecLoansFlow(flow)
  }

  return isSecLoansFlow(flow) ? flow : (undefined as OptionalReturnType<TRequired, SecLoansFlow>)
}

export const getSecLoansEntry = (): SecLoansEntry => {
  const entry = getEntry()
  return isEnumValue(entry, SecLoansEntry) ? entry : SecLoansEntry.Default
}

export const getLoanOfferId = (): string | null => {
  return getLocalStorageItem(QueryParams.LoanOfferId)
}

export const getAndStoreInputParams = (): InputParams => {
  const sourceChannel = resolveAndStoreSourceChannel()
  const flow = resolveAndStoreFlow()
  resolveAndStoreEntry()
  resolveAndStoreLanguage()
  resolveAndStoreLoanOfferId()
  return { flow, sourceChannel }
}

export const getIfpLoanByUuid = (ifpLoans: IfpLoan[], loanUuid: string): IfpLoan =>
  assertValue(
    ifpLoans.find((l) => l.uuid === loanUuid),
    `ifpLoans.find(loanUuid = ${loanUuid})`,
  )

export const getIfpLoanFromUserByUuid = (user: User, loanUuid: string): IfpLoan =>
  getIfpLoanByUuid(user.ifpLoans, loanUuid)

export const getIfpLoanByApplicationId = (
  user: User,
  applications: ApplicationsMetaData,
  applicationId: string,
): IfpLoan => {
  const metadata = assertValue(
    applications.ifpApplicationsMetadata?.find((m) => m.applicationId === applicationId),
    `user.ifpApplications.find(applicationId = ${applicationId})`,
  )
  return getIfpLoanFromUserByUuid(user, metadata.loan.uuid)
}

export const getAvailableIfpLoans = (ifpLoans: IfpLoan[]): IfpLoan[] => ifpLoans.filter((l) => l.isAvailable)

export const secLoansExit = async (params: { language: Language; isSuccess: boolean }): Promise<void> => {
  const { language, isSuccess } = params
  let exitUrl
  try {
    const response = await callEndpoint(ENDPOINT_SEC_LOANS_EXIT, { requestPayload: { isSuccess } })
    exitUrl = response.exitUrl
  } catch (error) {
    debugLog(error)
    exitUrl = FALLBACK_EXIT_URLS[language]
  }
  await commonExit(exitUrl)
}
